import {
  CategoryDisplayName,
  getCategoryByName,
} from '@oxappsec/ox-consolidated-categories';
import { Location, NavigateFunction } from 'react-router-dom';
import { appcuesPage } from '../../telemetry/appcues';
import { FilterTypeToFilter } from '../filters-utils/filters-utils';
import { logger } from 'logging-utils';
import { OneOfValues } from 'common-types';

let _navigate: NavigateFunction;
let _location: Location;

const history = () => {
  if (!_navigate || !_location) {
    throw new Error('app-navigator has not been initialized.');
  }
  return { _navigate, _location };
};

export const currentPage = (): OneOfValues<typeof AppPages> => {
  const { pathname } = history()._location;
  return pathname;
};

export const navigate = (
  page: OneOfValues<typeof AppPages>,
  queryString = '',
): void => {
  const { pathname: currentPath, search: currentQueryString } =
    history()._location;
  const pathChanged = currentPath !== page;
  const queryStringChanged = currentQueryString !== queryString;
  const canPushState = pathChanged || queryStringChanged;

  if (canPushState) {
    history()._navigate({ pathname: page, search: queryString });
  }
};

export const navigateToIssues = (filters: {
  categories?: string[];
  criticality?: string[];
  apps?: string[];
}) => {
  try {
    const categoriesToUse = filters.categories?.map(category => {
      const categoryObj = getCategoryByName(category as CategoryDisplayName);
      return categoryObj ? `${categoryObj.id}` : category;
    });

    const encodedFilters = encodeURI(
      JSON.stringify({ ...filters, categories: categoriesToUse }).replace(
        /%(?![0-9][0-9a-fA-F]+)/g,
        '%25',
      ),
    );
    navigate(AppPages.Issues, `filters=${encodedFilters}`);
  } catch (e) {
    logger.error('Failed to navigate to issues page', e);
  }
};

export const navigateToIssuesByApp = (
  filterBy: FilterTypeToFilter,
  value: string,
  app: string,
) => {
  let filters = {};
  filters[filterBy] = [value];
  filters['apps'] = [app];
  try {
    const encodedFilters = encodeURI(JSON.stringify(filters));
    navigate(AppPages.Issues, `filters=${encodedFilters}`);
  } catch (e) {
    logger.error('Failed to navigate to issues page', e);
  }
};

export const navigateToResolvedIssues = (filters: {}) => {
  try {
    const encodedFilters = encodeURI(JSON.stringify(filters));
    navigate(AppPages.ResolvedIssues, `filters=${encodedFilters}`);
  } catch (e) {
    logger.error('Failed to navigate to issues page', e);
  }
};

export const navigateWithFilters = (page: string, filters: {}) => {
  try {
    const encodedFilters = encodeURI(JSON.stringify(filters));
    navigate(page, `filters=${encodedFilters}`);
  } catch (e) {
    logger.error('Failed to navigate to page', e);
  }
};

export const navigateToPipelineIssues = (filters: {}) => {
  try {
    const encodedFilters = encodeURI(JSON.stringify(filters));
    navigate(AppPages.PipelineIssues, `filters=${encodedFilters}`);
  } catch (e) {
    logger.error('Failed to navigate to issues page', e);
  }
};

export const navigateToPipelineSummaryIssues = (filters: {}) => {
  try {
    const encodedFilters = encodeURI(JSON.stringify(filters));
    navigate(AppPages.PipelineSummary, `filters=${encodedFilters}`);
  } catch (e) {
    logger.error('Failed to navigate to issues page', e);
  }
};

export const navigateToApps = (filters: {}) => {
  try {
    const encodedFilters = encodeURI(JSON.stringify(filters));
    navigate(AppPages.Applications, `filters=${encodedFilters}`);
  } catch (e) {
    logger.error('Failed to navigate to applications page', e);
  }
};

export const navigateToArtifacts = (filters: {}) => {
  try {
    const encodedFilters = encodeURI(JSON.stringify(filters));
    navigate(AppPages.Artifacts, `filters=${encodedFilters}`);
  } catch (e) {
    logger.error('Failed to navigate to artifacts page', e);
  }
};

export const navigateToExclusions = (filters: {}) => {
  try {
    const encodedFilters = encodeURI(JSON.stringify(filters));
    navigate(AppPages.Exclusions, `filters=${encodedFilters}`);
  } catch (e) {
    logger.error('Failed to navigate to exclusions page', e);
  }
};

export const navigateToSbom = (filters: {}) => {
  try {
    const encodedFilters = encodeURI(JSON.stringify(filters));
    navigate(AppPages.Sbom, `filters=${encodedFilters}`);
  } catch (e) {
    logger.error('Failed to navigate to artifacts page', e);
  }
};

export const navigateToConnectors = () => {
  navigate(AppPages.Connectors);
};

export const createSpecificAppDetailsLink = (appName: string) => {
  return (
    AppPages.Issues +
    `?filters=${encodeURI(JSON.stringify({ apps: [appName] }))}`
  );
};

export const navigateTospesificPolicy = (
  categoryId: string,
  policyId: string,
) => {
  return AppPages.Policies + `?categoryId=${categoryId}&policyId=${policyId}`;
};

export const navigateTospesificApp = (appId: string) => {
  return AppPages.Applications + `?appId=${appId}`;
};

export const navigateToSpecificApp = (appId: string) => {
  appId && navigate(AppPages.Applications, `appId=${appId}`);
};

export const getQueryString = () => {
  return new URLSearchParams(history()._location.search);
};

export const setupRoutingObjects = (
  navigate: NavigateFunction,
  location: Location,
) => {
  _navigate = navigate;
  _location = location;
  appcuesPage();
};

export const useQuery = (): URLSearchParams => {
  const queryString = history()._location.search;
  const urlParams = new URLSearchParams(queryString);
  return urlParams;
};

export const AppPages = {
  Home: '/',
  Applications: '/applications',
  Connectors: '/connectors',
  Settings: '/settings',
  Policies: '/policies',
  Members: '/members',
  IrrelevantApps: '/irrelevant-apps',
  Exclusions: '/exclusions',
  Unauthorized: '/unauthorized',
  InviteOnly: '/invite-only',
  OrgLogin: '/login',
  OrgSignup: '/signup',
  IDPConfigure: '/identity-provider-configure',
  Onboarding: '/onboarding',
  Dashboard: '/dashboard',
  Issues: '/issues',
  ResolvedIssues: '/resolved-issues',
  PipelineIssues: '/pipeline-issues',
  EmailVerification: 'email-verification',
  Sbom: '/sbom',
  Artifacts: '/artifacts',
  PipelineSummary: '/pipeline-summary',
  AuditLogs: '/audit-logs',
  ScanHistory: '/scan-history',
};
